import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Input,
  Form,
  message,
  Modal,
  Spin,
  Tooltip,
} from "antd";
import { DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import axiosInstance from "../utils/axiosInstance";

const { confirm } = Modal;

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [form] = Form.useForm();
  const [addModalVisible, setAddModalVisible] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get("/category/categories");
        if (response.data.success) {
          const formattedCategories = response.data.categories.map((cat) => ({
            key: cat.id.toString(),
            name: cat.category_name,
          }));
          setCategories(formattedCategories);
        } else {
          message.error("Failed to fetch categories.");
        }
      } catch (error) {
        message.error("An error occurred while fetching categories.");
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const handleAddCategory = async (values) => {
    try {
      const response = await axiosInstance.post("/admin/category", {
        category_name: values.name,
      });

      if (response.status === 201 && response.data.success) {
        const newCategory = {
          key: response.data.category.id.toString(),
          name: response.data.category.category_name,
        };

        setCategories((prevCategories) => [...prevCategories, newCategory]);
        message.success("Category added successfully!");
        setAddModalVisible(false);
        form.resetFields();
      } else {
        message.error("Failed to add category.");
      }
    } catch (error) {
      message.error("An error occurred while adding the category.");
    }
  };

  const handleDeleteCategory = (key) => {
    confirm({
      title: "Are you sure you want to delete this category?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          const response = await axiosInstance.delete(`/admin/category/${key}`);
          if (response.data.success) {
            setCategories((prev) => prev.filter((cat) => cat.key !== key));
            message.success("Category deleted successfully!");
          } else {
            message.error("Failed to delete category.");
          }
        } catch (error) {
          message.error("An error occurred while deleting the category.");
        }
      },
    });
  };

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: "Sr.No",
      key: "srno",
      width: 70,
      render: (text, record, index) => (
        <div className="text-center font-medium text-gray-600">
          {index + 1}
        </div>
      ),
    },
    {
      title: "Category Name",
      dataIndex: "name",
      key: "name",
      align: "left",
      render: (name) => (
        <div className="text-sm font-medium text-gray-900">
          {name}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Delete Category">
          <Button
            type="primary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteCategory(record.key)}
            size="small"
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Manage Categories</h1>
                <p className="text-gray-600">Organize and manage product categories</p>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search Input */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <SearchOutlined className="mr-2" />
                  Search Categories
                </label>
                <Input
                  placeholder="Search category name..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  prefix={<SearchOutlined className="text-gray-400" />}
                  className="w-full h-10 rounded-lg"
                  allowClear
                />
              </div>

              {/* Stats Card */}
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 flex items-center mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                  Category Summary
                </label>
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-3 h-10 flex items-center">
                  <div className="text-sm font-medium text-gray-900">
                    Total: {filteredCategories.length} categories
                  </div>
                </div>
              </div>

              {/* Add Button */}
              <div className="flex flex-col justify-end">
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={() => setAddModalVisible(true)}
                  className="h-10 bg-blue-600 hover:bg-blue-700 border-blue-600"
                >
                  Add New Category
                </Button>
              </div>
            </div>
          </div>

          {/* Table Section */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <Table
              dataSource={filteredCategories}
              columns={columns}
              rowKey="key"
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => 
                  `${range[0]}-${range[1]} of ${total} items`,
                className: "px-6 py-4",
                pageSizeOptions: ['10', '20', '50']
              }}
              scroll={{ x: 'max-content' }}
              className="modern-orders-table"
              rowClassName={(record) => "hover:bg-gray-50 transition-colors duration-200"}
              locale={{
                emptyText: (
                  <div className="text-center py-8">
                    <div className="text-lg font-medium text-gray-500 mb-2">No categories found</div>
                    <div className="text-sm text-gray-400">Try adding a new category or adjusting your search</div>
                  </div>
                )
              }}
            />
          </div>
        </div>
      </div>

      {/* Add Category Modal */}
      <Modal
        title={
          <div className="flex items-center space-x-2">
            <PlusOutlined className="text-blue-500" />
            <span>Add New Category</span>
          </div>
        }
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        className="modern-modal"
      >
        <Form form={form} layout="vertical" onFinish={handleAddCategory}>
          <Form.Item
            label={
              <span className="text-sm font-medium text-gray-700">
                Category Name
              </span>
            }
            name="name"
            rules={[{ required: true, message: "Please enter a category name!" }]}
          >
            <Input 
              placeholder="Enter category name" 
              size="large"
              className="rounded-lg h-12"
            />
          </Form.Item>
          <Form.Item className="mb-0">
            <div className="flex justify-end space-x-3">
              <Button 
                onClick={() => {
                  setAddModalVisible(false);
                  form.resetFields();
                }}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                className="bg-blue-600 hover:bg-blue-700 border-blue-600 rounded-lg"
              >
                Add Category
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>

      <style jsx>{`
        .modern-orders-table .ant-table-thead > tr > th {
          background: #f8fafc;
          color: #374151;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 12px 16px;
        }
        
        .modern-orders-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 12px 16px;
          transition: all 0.2s ease;
        }
        
        .modern-modal .ant-modal-content {
          border-radius: 16px;
          overflow: hidden;
        }
        
        .modern-modal .ant-modal-header {
          border-bottom: 1px solid #e5e7eb;
          background: #f8fafc;
        }
        
        .ant-input {
          border-radius: 8px !important;
        }
      `}</style>
    </Spin>
  );
};

export default CategoryManager;